import { useCallback, useRef } from "react";

interface MediaProgressBarProps {
  currentTime: number;
  duration: number;
  progressPercentage: number;
  disableSeeking?: boolean;
  onSeek: (time: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  formatTime: (time: number) => string;
  className?: string;
  progressBarBg?: string;
  progressFillBg?: string;
  progressHandleBg?: string;
  timeTextColor?: string;
  progressBarHeight?: string;
  progressHandleSize?: string;
}

const MediaProgressBar = ({
  currentTime,
  duration,
  progressPercentage,
  disableSeeking = false,
  onSeek,
  onDragStart,
  onDragEnd,
  formatTime,
  className = "",
  progressBarBg = "bg-soft-clay",
  progressFillBg = "bg-white",
  progressHandleBg = "bg-primary",
  timeTextColor = "text-primary",
  progressBarHeight = "h-1.5",
  progressHandleSize = "w-2.5 h-2.5",
}: MediaProgressBarProps) => {
  const progressBarRef = useRef<HTMLDivElement>(null);

  const updateTimeFromClientX = useCallback(
    (clientX: number) => {
      const progressBar = progressBarRef.current;
      if (!progressBar || duration <= 0) return;

      const rect = progressBar.getBoundingClientRect();
      const offsetX = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const ratio = rect.width === 0 ? 0 : offsetX / rect.width;
      const newTime = ratio * duration;

      onSeek(newTime);
    },
    [duration, onSeek]
  );

  const handleProgressClick = (e: React.MouseEvent) => {
    if (disableSeeking) return;
    updateTimeFromClientX(e.clientX);
  };

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disableSeeking) return;

      onDragStart();
      updateTimeFromClientX(e.clientX);

      const handlePointerMove = (event: PointerEvent) => {
        updateTimeFromClientX(event.clientX);
      };

      const handlePointerUp = () => {
        onDragEnd();
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("pointercancel", handlePointerUp);
      };

      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);
    },
    [disableSeeking, updateTimeFromClientX, onDragStart, onDragEnd]
  );

  return (
    <div className={`space-y-2 ${className}`}>
      <div
        ref={progressBarRef}
        className={`relative ${progressBarHeight} ${progressBarBg} rounded-full touch-none ${
          disableSeeking ? "cursor-default" : "cursor-pointer"
        }`}
        onClick={disableSeeking ? undefined : handleProgressClick}
        onPointerDown={disableSeeking ? undefined : handlePointerDown}
      >
        <div className="absolute inset-x-0 -inset-y-2" />
        {/* Progress fill */}
        <div
          className={`absolute left-0 top-0 h-full ${progressFillBg} rounded-full transition-all duration-150`}
          style={{ width: `${progressPercentage}%` }}
        />
        {/* Progress handle */}
        <div
          className={`absolute top-1/2 transform -translate-y-1/2 ${progressHandleSize} ${progressHandleBg} rounded-full ${
            disableSeeking
              ? "cursor-default"
              : "cursor-grab active:cursor-grabbing"
          }`}
          style={{ left: `calc(${progressPercentage}% - 8px)` }}
        />
      </div>

      {/* Time display */}
      <div className={`flex justify-between text-micro ${timeTextColor}`}>
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

export default MediaProgressBar;
